import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Users, TrendingUp, Mail, Send, Clock, Shield, CheckCircle, Lightbulb, Bell, AlertCircle, Search, Check, CheckCheck, Info, MoreVertical, X, Upload, FileText, ArrowUpCircle, Smartphone, Package, User, XCircle, Plus, Archive } from 'lucide-react';
import Navigation from '../../components/Navigation';
import StandardCard from '../../components/ui/StandardCard';
import StandardButton from '../../components/ui/StandardButton';
import StandardInput from '../../components/ui/StandardInput';
import StandardSelect from '../../components/ui/StandardSelect';
import StandardTextarea from '../../components/ui/StandardTextarea';

// Import services
import { messageService } from '../../services/messageService';
import { negotiationService } from '../../services/negotiationService';
import { notificationPreferencesService } from '../../services/notificationPreferencesService';
import { disputeService } from '../../services/disputeService';
import { discussionService } from '../../services/discussionService';
import { unreadCounterService } from '../../services/unreadCounterService';
import { communityStatsService } from '../../services/communityStatsService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { sr } from 'date-fns/locale';

const Community = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Determine initial tab from location state or default to 'email'
  const initialTab = location?.state?.tab || 'email';
  
  const [searchTerm, setSearchTerm] = useState('');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sentEmails, setSentEmails] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Direct Communication states
  const [negotiations, setNegotiations] = useState([]);
  const [selectedNegotiation, setSelectedNegotiation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageSearchTerm, setMessageSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Negotiations Dashboard states
  const [negotiationsList, setNegotiationsList] = useState([]);
  const [filteredNegotiations, setFilteredNegotiations] = useState([]);
  const [negotiationFilters, setNegotiationFilters] = useState({
    status: 'all',
    searchTerm: ''
  });
  const [analytics, setAnalytics] = useState({
    total: 0,
    initiated: 0,
    inProgress: 0,
    agreed: 0,
    completed: 0,
    cancelled: 0
  });

  // Notification Preferences states
  const [preferences, setPreferences] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [notificationTab, setNotificationTab] = useState('trading');

  // Dispute Resolution states
  const [disputes, setDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [disputeFilters, setDisputeFilters] = useState({
    status: '',
    category: '',
    priority: ''
  });
  const [disputeTab, setDisputeTab] = useState('overview');
  const [uploadingEvidence, setUploadingEvidence] = useState(false);

  // New dispute form states
  const [showNewDisputeForm, setShowNewDisputeForm] = useState(false);
  const [userNegotiations, setUserNegotiations] = useState([]);
  const [newDisputeForm, setNewDisputeForm] = useState({
    negotiationId: '',
    respondentId: '',
    category: '',
    priority: 'medium',
    title: '',
    description: '',
    desiredResolution: ''
  });
  const [newDisputeFile, setNewDisputeFile] = useState(null);
  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [disputeSuccess, setDisputeSuccess] = useState('');

  // Discussion states
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsError, setTopicsError] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicDetail, setTopicDetail] = useState(null);
  const [topicDetailLoading, setTopicDetailLoading] = useState(false);
  const [showNewTopicForm, setShowNewTopicForm] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicBody, setNewTopicBody] = useState('');
  const [submittingTopic, setSubmittingTopic] = useState(false);
  const [newCommentBody, setNewCommentBody] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [discussionSuccess, setDiscussionSuccess] = useState('');

  // Best Members states
  const [bestMembers, setBestMembers] = useState([]);
  const [bestMembersLoading, setBestMembersLoading] = useState(false);
  const [bestMembersError, setBestMembersError] = useState('');

  // Stats states
  const [statsPeriod, setStatsPeriod] = useState('all');
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');

  // Email templates for manual sending
  const emailTemplates = [
    {
      value: '',
      label: t('community.email.templatePlaceholder')
    },
    {
      value: 'empty',
      label: t('community.email.templates.empty'),
      subject: '',
      body: ''
    },
    {
      value: 'accept_offer',
      label: t('community.email.templates.accept_offer'),
      subject: t('community.email.templates.accept_offer_subject'),
      body: t('community.email.templates.accept_offer_body')
    },
    {
      value: 'payment_instructions',
      label: t('community.email.templates.payment_instructions'),
      subject: t('community.email.templates.payment_instructions_subject'),
      body: t('community.email.templates.payment_instructions_body')
    },
    {
      value: 'voucher_inquiry',
      label: t('community.email.templates.voucher_inquiry'),
      subject: t('community.email.templates.voucher_inquiry_subject'),
      body: t('community.email.templates.voucher_inquiry_body')
    },
    {
      value: 'exchange_offer',
      label: t('community.email.templates.exchange_offer'),
      subject: t('community.email.templates.exchange_offer_subject'),
      body: t('community.email.templates.exchange_offer_body')
    },
    {
      value: 'price_negotiation',
      label: t('community.email.templates.price_negotiation'),
      subject: t('community.email.templates.price_negotiation_subject'),
      body: t('community.email.templates.price_negotiation_body')
    },
    {
      value: 'verification_request',
      label: t('community.email.templates.verification_request'),
      subject: t('community.email.templates.verification_request_subject'),
      body: t('community.email.templates.verification_request_body')
    },
    {
      value: 'payment_confirmation',
      label: t('community.email.templates.payment_confirmation'),
      subject: t('community.email.templates.payment_confirmation_subject'),
      body: t('community.email.templates.payment_confirmation_body')
    },
    {
      value: 'receipt_confirmation',
      label: t('community.email.templates.receipt_confirmation'),
      subject: t('community.email.templates.receipt_confirmation_subject'),
      body: t('community.email.templates.receipt_confirmation_body')
    },
    {
      value: 'problem_report',
      label: t('community.email.templates.problem_report'),
      subject: t('community.email.templates.problem_report_subject'),
      body: t('community.email.templates.problem_report_body')
    },
    {
      value: 'thank_you_rating',
      label: t('community.email.templates.thank_you_rating'),
      subject: t('community.email.templates.thank_you_rating_subject'),
      body: t('community.email.templates.thank_you_rating_body')
    }
  ];

  const tabs = [
    { id: 'email', label: t('community.tabs.email'), icon: Mail },
    { id: 'messages', label: t('community.tabs.messages'), icon: MessageSquare },
    { id: 'negotiations', label: t('community.tabs.negotiations'), icon: TrendingUp },
    { id: 'notifications', label: t('community.tabs.notifications'), icon: Bell },
    { id: 'disputes', label: t('community.tabs.disputes'), icon: AlertCircle },
    { id: 'discussions', label: t('community.tabs.discussions'), icon: MessageSquare },
    { id: 'members', label: t('community.tabs.members'), icon: Users },
    { id: 'stats', label: t('community.tabs.stats'), icon: TrendingUp },
    { id: 'tips', label: t('community.tabs.tips'), icon: Lightbulb }
  ];

  // Effect to handle tab changes from navigation
  useEffect(() => {
    if (location?.state?.tab) {
      setActiveTab(location?.state?.tab);
    }
  }, [location?.state?.tab]);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'messages' && user?.id) {
      loadNegotiations();
    } else if (activeTab === 'negotiations' && user?.id) {
      loadNegotiationsList();
    } else if (activeTab === 'notifications' && user?.id) {
      loadPreferences();
    } else if (activeTab === 'disputes' && user?.id) {
      loadDisputes();
    } else if (activeTab === 'discussions') {
      loadTopics();
    } else if (activeTab === 'members') {
      loadBestMembers();
    } else if (activeTab === 'stats') {
      loadStats('all');
      setStatsPeriod('all');
    }
  }, [activeTab, user?.id]);

  // Load unread counts
  const loadUnreadCounts = async () => {
    if (!user?.id) return;
    
    try {
      const counts = await unreadCounterService?.getAllUnreadCounts(user?.id);
      if (counts) {
        setUnreadCounts(counts);
      }
    } catch (error) {
      console.error('Error loading unread counts:', error);
    }
  };

  // Load unread counts on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      loadUnreadCounts();

      // Subscribe to real-time updates
      const unsubscribe = unreadCounterService?.subscribeToUnreadUpdates(
        user?.id,
        (counts) => {
          if (counts) {
            setUnreadCounts(counts);
          }
        }
      );

      // Cleanup subscription on unmount
      return () => {
        unsubscribe();
      };
    }
  }, [user?.id]);

  // Refresh counts when returning to tab or marking messages as read
  useEffect(() => {
    if (user?.id && activeTab) {
      loadUnreadCounts();
    }
  }, [activeTab, user?.id]);

  // Direct Communication functions
  const loadNegotiations = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const data = await messageService?.getUserNegotiations(user?.id);
      setNegotiations(data || []);
      if (data && data?.length > 0 && !selectedNegotiation) {
        setSelectedNegotiation(data?.[0]);
      }
    } catch (error) {
      console.error('Error loading negotiations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedNegotiation) {
      loadMessages(selectedNegotiation?.id);
      messageService?.markMessagesAsRead(selectedNegotiation?.id, user?.id);

      const messageChannel = messageService?.subscribeToMessages(
        selectedNegotiation?.id,
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            loadMessages(selectedNegotiation?.id);
          }
        }
      );

      const typingChannel = messageService?.subscribeToTypingIndicators(
        selectedNegotiation?.id,
        (typingStatus) => {
          setTypingUsers(typingStatus || []);
        }
      );

      loadTypingStatus(selectedNegotiation?.id);

      return () => {
        messageService?.unsubscribeFromMessages(messageChannel);
        messageService?.unsubscribeFromTypingIndicators(typingChannel);
      };
    }
  }, [selectedNegotiation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, typingUsers]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTypingStatus = async (negotiationId) => {
    try {
      const status = await messageService?.getTypingStatus(negotiationId);
      setTypingUsers(status || []);
    } catch (error) {
      console.error('Error loading typing status:', error);
    }
  };

  const loadMessages = async (negotiationId) => {
    try {
      const data = await messageService?.getNegotiationMessages(negotiationId);
      setMessages(data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (messageText = null, messageType = 'text') => {
    const textToSend = messageText || newMessage?.trim();
    if (!textToSend || !selectedNegotiation) return;

    setSending(true);
    try {
      await messageService?.sendMessage(
        selectedNegotiation?.id,
        textToSend,
        messageType
      );
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert(t('community.errors.sendMessage'));
    } finally {
      setSending(false);
    }
  };

  const handleQuickAction = (action) => {
    handleSendMessage(action?.text, action?.type);
  };

  const handleMessageSearch = async () => {
    if (!messageSearchTerm?.trim() || !user?.id) return;

    setLoading(true);
    try {
      const results = await messageService?.searchMessages(user?.id, messageSearchTerm);
      setSearchResults(results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching messages:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = async (e) => {
    setNewMessage(e?.target?.value);

    if (typingTimeoutRef?.current) {
      clearTimeout(typingTimeoutRef?.current);
    }

    if (selectedNegotiation && e?.target?.value?.trim()) {
      await messageService?.setTypingStatus(selectedNegotiation?.id, true);
      typingTimeoutRef.current = setTimeout(async () => {
        await messageService?.setTypingStatus(selectedNegotiation?.id, false);
      }, 3000);
    } else if (selectedNegotiation) {
      await messageService?.setTypingStatus(selectedNegotiation?.id, false);
    }
  };

  const closeSearchResults = () => {
    setShowSearchResults(false);
    setSearchResults([]);
    setMessageSearchTerm('');
  };

  const selectSearchResult = (message) => {
    const negotiation = negotiations?.find(n => n?.id === message?.negotiationId);
    if (negotiation) {
      setSelectedNegotiation(negotiation);
      closeSearchResults();
    }
  };

  const getOtherParticipant = (negotiation) => {
    if (!negotiation || !user?.id) return null;
    return negotiation?.initiatorId === user?.id 
      ? negotiation?.responder 
      : negotiation?.initiator;
  };

  // Negotiations Dashboard functions
  const loadNegotiationsList = async () => {
    try {
      setLoading(true);
      const data = await negotiationService?.getUserNegotiations();
      setNegotiationsList(data);
      calculateAnalytics(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (data) => {
    const stats = {
      total: data?.length,
      initiated: data?.filter(n => n?.status === 'initiated')?.length,
      inProgress: data?.filter(n => n?.status === 'in_progress')?.length,
      agreed: data?.filter(n => n?.status === 'agreed')?.length,
      completed: data?.filter(n => n?.status === 'completed')?.length,
      cancelled: data?.filter(n => n?.status === 'cancelled')?.length
    };
    setAnalytics(stats);
  };

  useEffect(() => {
    applyNegotiationFilters();
  }, [negotiationsList, negotiationFilters]);

  const applyNegotiationFilters = () => {
    let filtered = [...negotiationsList];

    if (negotiationFilters?.status !== 'all') {
      filtered = filtered?.filter(n => n?.status === negotiationFilters?.status);
    }

    if (negotiationFilters?.searchTerm) {
      const term = negotiationFilters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(n => 
        n?.voucherTitle?.toLowerCase()?.includes(term) ||
        n?.counterpartyName?.toLowerCase()?.includes(term)
      );
    }

    filtered?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    setFilteredNegotiations(filtered);
  };

  const handleNegotiationClick = (negotiation) => {
    setSelectedNegotiation(negotiation);
    setActiveTab('messages');
  };

  // Notification Preferences functions
  const loadPreferences = async () => {
    try {
      setLoading(true);
      const data = await notificationPreferencesService?.getUserPreferences();
      setPreferences(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (field) => {
    try {
      const updated = { ...preferences, [field]: !preferences?.[field] };
      setPreferences(updated);
      await notificationPreferencesService?.updatePreferences(updated);
      showSuccess(t('community.notifications.settingsUpdated'));
    } catch (err) {
      setError(err?.message);
      loadPreferences();
    }
  };

  const handleBulkToggle = async (category, enabled) => {
    try {
      setSaving(true);
      const data = await notificationPreferencesService?.bulkToggleCategory(category, enabled);
      setPreferences(data);
      showSuccess(enabled ? t('community.notifications.categoryEnabled') : t('community.notifications.categoryDisabled'));
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdvancedUpdate = async (field, value) => {
    try {
      const updated = { ...preferences, [field]: value };
      setPreferences(updated);
      await notificationPreferencesService?.updateAdvancedSettings({
        notificationFrequency: updated?.notificationFrequency,
        quietHoursStart: updated?.quietHoursStart,
        quietHoursEnd: updated?.quietHoursEnd,
        priorityUrgentOnly: updated?.priorityUrgentOnly
      });
      showSuccess(t('community.notifications.advancedUpdated'));
    } catch (err) {
      setError(err?.message);
    }
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  // Dispute Resolution functions
  const loadDisputes = async () => {
    try {
      setLoading(true);
      const data = await disputeService?.getUserDisputes(disputeFilters);
      setDisputes(data);
      setError('');
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const loadUserNegotiationsForDispute = async () => {
    if (!user?.id) return;
    try {
      const data = await negotiationService?.getMarketplaceNegotiations();
      setUserNegotiations(data || []);
    } catch (err) {
      console.error('Error loading negotiations for dispute:', err);
    }
  };

  const handleNewDisputeNegotiationChange = (negotiationId) => {
    const neg = userNegotiations?.find(n => n?.id === negotiationId);
    const respondentId = neg ? (neg?.buyerId === user?.id ? neg?.sellerId : neg?.buyerId) : '';
    setNewDisputeForm(prev => ({ ...prev, negotiationId, respondentId }));
  };

  const handleSubmitNewDispute = async () => {
    if (!newDisputeForm?.respondentId || !newDisputeForm?.category || !newDisputeForm?.title || !newDisputeForm?.description) {
      setError(t('community.disputes.form.validationError'));
      return;
    }
    setSubmittingDispute(true);
    setError('');
    try {
      const created = await disputeService?.createDispute({
        negotiationId: newDisputeForm?.negotiationId || null,
        respondentId: newDisputeForm?.respondentId,
        category: newDisputeForm?.category,
        priority: newDisputeForm?.priority,
        title: newDisputeForm?.title,
        description: newDisputeForm?.description,
        desiredResolution: newDisputeForm?.desiredResolution
      });
      if (newDisputeFile && created?.id) {
        await disputeService?.uploadEvidence(
          created?.id,
          newDisputeFile,
          t('community.disputes.form.evidenceHint'),
          getEvidenceTypeFromFile(newDisputeFile)
        );
      }
      setDisputeSuccess(t('community.disputes.successMsg'));
      setShowNewDisputeForm(false);
      setNewDisputeForm({ negotiationId: '', respondentId: '', category: '', priority: 'medium', title: '', description: '', desiredResolution: '' });
      setNewDisputeFile(null);
      await loadDisputes();
      setTimeout(() => setDisputeSuccess(''), 5000);
    } catch (err) {
      setError(err?.message || t('community.disputes.submitError'));
    } finally {
      setSubmittingDispute(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'disputes' && user?.id) {
      loadDisputes();
    }
  }, [disputeFilters, activeTab, user?.id]);

  useEffect(() => {
    if (selectedDispute) {
      loadDisputeDetails(selectedDispute?.id);
    }
  }, [selectedDispute]);

  const loadDisputeDetails = async (disputeId) => {
    try {
      const [evidenceData, timelineData] = await Promise.all([
        disputeService?.getDisputeEvidence(disputeId),
        disputeService?.getDisputeTimeline(disputeId)
      ]);
      setEvidence(evidenceData);
      setTimeline(timelineData);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleEvidenceUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || !selectedDispute) return;

    setUploadingEvidence(true);
    try {
      await disputeService?.uploadEvidence(
        selectedDispute?.id,
        file,
        '',
        getEvidenceTypeFromFile(file)
      );
      await loadDisputeDetails(selectedDispute?.id);
      setError('');
    } catch (err) {
      setError(`Upload failed: ${err?.message}`);
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleEscalate = async (disputeId) => {
    try {
      await disputeService?.escalateDispute(disputeId, 'User requested escalation to mediation team');
      await loadDisputes();
      if (selectedDispute?.id === disputeId) {
        const updated = await disputeService?.getDisputeById(disputeId);
        setSelectedDispute(updated);
      }
    } catch (err) {
      setError(err?.message);
    }
  };

  const getEvidenceTypeFromFile = (file) => {
    if (file?.type?.startsWith('image/')) return 'screenshot';
    if (file?.type === 'application/pdf') return 'document';
    if (file?.type?.startsWith('video/')) return 'video';
    return 'other';
  };

  // Utility functions
  const getStatusColor = (status) => {
    const colors = {
      initiated: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
            agreed: 'bg-blue-100 text-blue-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
      filed: 'bg-yellow-100 text-yellow-800',
      under_review: 'bg-blue-100 text-blue-800',
      awaiting_evidence: 'bg-orange-100 text-orange-800',
      mediator_assigned: 'bg-purple-100 text-purple-800',
      in_mediation: 'bg-indigo-100 text-indigo-800',
      escalated: 'bg-red-100 text-red-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      initiated: t('community.negotiations.initiated'),
      in_progress: t('community.negotiations.inProgress'),
      agreed: t('community.negotiations.agreed'),
      completed: t('community.negotiations.completed'),
      cancelled: t('community.negotiations.cancelled')
    };
    return labels?.[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      initiated: <Clock className="w-4 h-4" />,
      in_progress: <MessageSquare className="w-4 h-4" />,
      agreed: <CheckCircle className="w-4 h-4" />,
      completed: <CheckCircle className="w-4 h-4" />,
      cancelled: <XCircle className="w-4 h-4" />
    };
    return icons?.[status] || <AlertCircle className="w-4 h-4" />;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-600',
      medium: 'text-blue-600',
      high: 'text-orange-600',
      urgent: 'text-red-600'
    };
    return colors?.[priority] || 'text-gray-600';
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('sr-RS', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimestamp = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('community.timestamps.justNow');
    if (minutes < 60) return t('community.timestamps.minutesAgo', { count: minutes });
    if (hours < 24) return t('community.timestamps.hoursAgo', { count: hours });
    if (days < 7) return t('community.timestamps.daysAgo', { count: days });
    return date?.toLocaleDateString();
  };

  const handleTemplateChange = (value) => {
    setSelectedTemplate(value);
    const template = emailTemplates?.find(t => t?.value === value);
    if (template && value !== '') {
      setEmailSubject(template?.subject || '');
      setEmailMessage(template?.body || '');
    }
  };

  const handleSendEmail = () => {
    if (!emailRecipient || !emailSubject) {
      alert(t('community.errors.sendEmailValidation'));
      return;
    }

    const mailtoLink = `mailto:${emailRecipient}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailMessage)}`;
    
    const newEmail = {
      id: Date.now(),
      recipient: emailRecipient,
      subject: emailSubject,
      timestamp: new Date()?.toLocaleString(),
      template: selectedTemplate ? emailTemplates?.find(t => t?.value === selectedTemplate)?.label : t('community.email.originalMessage')
    };
    
    setSentEmails([newEmail, ...sentEmails]);
    window.location.href = mailtoLink;
    
    setEmailRecipient('');
    setEmailSubject('');
    setEmailMessage('');
    setSelectedTemplate('');
  };

  const communityStats = [];

  const recentDiscussions = [];

  const tips = [
    {
      icon: Shield,
      title: t('community.tips.tip1Title'),
      description: t('community.tips.tip1Desc')
    },
    {
      icon: CheckCircle,
      title: t('community.tips.tip2Title'),
      description: t('community.tips.tip2Desc')
    },
    {
      icon: TrendingUp,
      title: t('community.tips.tip3Title'),
      description: t('community.tips.tip3Desc')
    }
  ];

  const tradingItems = [
    { key: 'NewOffers', label: t('community.notifications.items.NewOffers') },
    { key: 'PriceChanges', label: t('community.notifications.items.PriceChanges') },
    { key: 'TransactionConfirmations', label: t('community.notifications.items.TransactionConfirmations') }
  ];

  const wishItems = [
    { key: 'MatchingWishes', label: t('community.notifications.items.MatchingWishes') },
    { key: 'NegotiationUpdates', label: t('community.notifications.items.NegotiationUpdates') },
    { key: 'ProposalResponses', label: t('community.notifications.items.ProposalResponses') }
  ];

  const communityItems = [
    { key: 'ForumReplies', label: t('community.notifications.items.ForumReplies') },
    { key: 'Mentions', label: t('community.notifications.items.Mentions') },
    { key: 'Achievements', label: t('community.notifications.items.Achievements') }
  ];

  const systemItems = [
    { key: 'MaintenanceAlerts', label: t('community.notifications.items.MaintenanceAlerts') },
    { key: 'PolicyChanges', label: t('community.notifications.items.PolicyChanges') },
    { key: 'SecurityNotifications', label: t('community.notifications.items.SecurityNotifications') }
  ];

  // Unread counter state
  const [unreadCounts, setUnreadCounts] = useState({
    messages: 0,
    negotiations: 0,
    email: 0,
    disputes: 0
  });

  // Discussion functions
  const loadTopics = async () => {
    setTopicsLoading(true);
    setTopicsError('');
    try {
      const data = await discussionService?.getTopics();
      setTopics(data || []);
    } catch (err) {
      setTopicsError(err?.message || t('community.errors.loadingTopics'));
    } finally {
      setTopicsLoading(false);
    }
  };

  const loadTopicDetail = async (topicId) => {
    setTopicDetailLoading(true);
    try {
      const data = await discussionService?.getTopicWithComments(topicId);
      setTopicDetail(data);
    } catch (err) {
      setTopicsError(err?.message || t('community.errors.loadingTopic'));
    } finally {
      setTopicDetailLoading(false);
    }
  };

  const handleOpenTopic = (topic) => {
    setSelectedTopic(topic);
    setTopicDetail(null);
    loadTopicDetail(topic?.id);
  };

  const handleBackToTopics = () => {
    setSelectedTopic(null);
    setTopicDetail(null);
    setNewCommentBody('');
  };

  const handleCreateTopic = async () => {
    if (!newTopicTitle?.trim() || !newTopicBody?.trim()) return;
    setSubmittingTopic(true);
    try {
      const created = await discussionService?.createTopic({ title: newTopicTitle?.trim(), body: newTopicBody?.trim() });
      setTopics(prev => [created, ...prev]);
      setShowNewTopicForm(false);
      setNewTopicTitle('');
      setNewTopicBody('');
      setDiscussionSuccess(t('community.discussions.topicCreated'));
      setTimeout(() => setDiscussionSuccess(''), 3000);
    } catch (err) {
      setTopicsError(err?.message || t('community.errors.loadingTopics'));
    } finally {
      setSubmittingTopic(false);
    }
  };

  const handleAddComment = async () => {
    if (!newCommentBody?.trim() || !topicDetail?.id) return;
    setSubmittingComment(true);
    try {
      const comment = await discussionService?.addComment({ topicId: topicDetail?.id, body: newCommentBody?.trim() });
      setTopicDetail(prev => ({ ...prev, comments: [...(prev?.comments || []), comment] }));
      setNewCommentBody('');
      // Update comment count in topics list
      setTopics(prev => prev?.map(t => t?.id === topicDetail?.id ? { ...t, commentCount: (t?.commentCount || 0) + 1 } : t));
    } catch (err) {
      setTopicsError(err?.message || t('community.errors.loadingTopics'));
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm(t('community.discussions.confirmDeleteTopic'))) return;
    try {
      await discussionService?.deleteTopic(topicId);
      setTopics(prev => prev?.filter(t => t?.id !== topicId));
      if (selectedTopic?.id === topicId) handleBackToTopics();
    } catch (err) {
      setTopicsError(err?.message || t('community.errors.loadingTopics'));
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm(t('community.discussions.confirmDeleteComment'))) return;
    try {
      await discussionService?.deleteComment(commentId);
      setTopicDetail(prev => ({ ...prev, comments: prev?.comments?.filter(c => c?.id !== commentId) }));
    } catch (err) {
      setTopicsError(err?.message || t('community.errors.loadingTopics'));
    }
  };

  const renderCategorySection = (category, title, items) => {
    if (notificationTab !== category) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
          <div className="flex gap-2">
            <button
              onClick={() => handleBulkToggle(category, true)}
              disabled={saving}
              className="px-3 py-1 text-sm text-[#23395B] hover:bg-blue-50 rounded-md transition-colors"
            >
              <Check className="w-4 h-4 inline mr-1" />
              {t('community.notifications.enableAll')}
            </button>
            <button
              onClick={() => handleBulkToggle(category, false)}
              disabled={saving}
              className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
            >
              <X className="w-4 h-4 inline mr-1" />
              {t('community.notifications.disableAll')}
            </button>
          </div>
        </div>
        <div className="space-y-4">
          {items?.map((item) => (
            <div key={item?.key} className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="font-medium text-gray-900 mb-3">{item?.label}</h4>
              <div className="grid grid-cols-3 gap-4">
                {['email', 'sms', 'push']?.map((type) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {type === 'email' && <Mail className="w-4 h-4 text-gray-600" />}
                      {type === 'sms' && <MessageSquare className="w-4 h-4 text-gray-600" />}
                      {type === 'push' && <Smartphone className="w-4 h-4 text-gray-600" />}
                      <span className="text-sm text-gray-700 capitalize">{type}</span>
                    </div>
                    <button
                      onClick={() => handleToggle(`${type}${item?.key}`)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        preferences?.[`${type}${item?.key}`] ? 'bg-[#23395B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          preferences?.[`${type}${item?.key}`] ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Load best members ranked by average rating, then by completed deals
  const loadBestMembers = async () => {
    setBestMembersLoading(true);
    setBestMembersError('');
    try {
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        ?.from('user_profiles')
        ?.select('id, company_id, full_name, email, phone, other_info')
        ?.eq('role', 'user');

      if (profilesError) throw profilesError;

      if (!profiles || profiles?.length === 0) {
        setBestMembers([]);
        return;
      }

      // Fetch all ratings
      const { data: ratings, error: ratingsError } = await supabase
        ?.from('user_ratings')
        ?.select('rated_id, rating');

      if (ratingsError) throw ratingsError;

      // Fetch completed deals: archived marketplace_wishes (offer_status='archived')
      const { data: archivedOffers, error: offersError } = await supabase
        ?.from('marketplace_wishes')
        ?.select('user_id')
        ?.eq('offer_status', 'archived');

      if (offersError) throw offersError;

      // Fetch completed negotiations
      const { data: completedNegs, error: negsError } = await supabase
        ?.from('negotiations')
        ?.select('initiator_id, responder_id')
        ?.eq('status', 'completed');

      if (negsError) throw negsError;

      // Build rating stats per user
      const ratingMap = {};
      (ratings || [])?.forEach(r => {
        if (!ratingMap?.[r?.rated_id]) {
          ratingMap[r?.rated_id] = { sum: 0, count: 0 };
        }
        ratingMap[r?.rated_id].sum += r?.rating;
        ratingMap[r?.rated_id].count += 1;
      });

      // Build completed deals count per user
      const dealsMap = {};
      (archivedOffers || [])?.forEach(o => {
        if (o?.user_id) {
          dealsMap[o?.user_id] = (dealsMap?.[o?.user_id] || 0) + 1;
        }
      });
      (completedNegs || [])?.forEach(n => {
        if (n?.initiator_id) {
          dealsMap[n?.initiator_id] = (dealsMap?.[n?.initiator_id] || 0) + 1;
        }
        if (n?.responder_id) {
          dealsMap[n?.responder_id] = (dealsMap?.[n?.responder_id] || 0) + 1;
        }
      });

      // Build member list with stats
      const members = profiles?.map(p => {
        const rStats = ratingMap?.[p?.id];
        const avgRating = rStats ? Math.round((rStats?.sum / rStats?.count) * 10) / 10 : null;
        const ratingCount = rStats ? rStats?.count : 0;
        const completedDeals = dealsMap?.[p?.id] || 0;
        return {
          id: p?.id,
          valuebookId: p?.company_id || '—',
          fullName: p?.full_name || t('community.messages.userFallback'),
          email: p?.email || '',
          otherInfo: p?.other_info || '',
          avgRating,
          ratingCount,
          completedDeals,
        };
      });

      // Filter: only show members who have at least one rating or one deal
      const ranked = members
        ?.filter(m => m?.avgRating !== null || m?.completedDeals > 0)
        ?.sort((a, b) => {
          // Primary: higher average rating first (null = lowest)
          const rA = a?.avgRating ?? -1;
          const rB = b?.avgRating ?? -1;
          if (rB !== rA) return rB - rA;
          // Secondary: more completed deals first
          return b?.completedDeals - a?.completedDeals;
        });

      setBestMembers(ranked);
    } catch (err) {
      console.error('Error loading best members:', err);
      setBestMembersError(t('community.errors.loadingMembers'));
    } finally {
      setBestMembersLoading(false);
    }
  };

  const loadStats = async (period) => {
    setStatsLoading(true);
    setStatsError('');
    try {
      const data = await communityStatsService?.getStats(period);
      setStatsData(data);
    } catch (err) {
      console.error('Error loading stats:', err);
      setStatsError(t('community.stats.loadError'));
    } finally {
      setStatsLoading(false);
    }
  };

  const handleStatsPeriodChange = (period) => {
    setStatsPeriod(period);
    loadStats(period);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#456A9E] to-[#5A7AB0] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto">
              {t('community.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>
      {/* Horizontal Tab Navigation */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto">
            {tabs?.map((tab) => {
              const IconComponent = tab?.icon;
              const count = unreadCounts?.[tab?.id] || 0;
              const showBadge = count > 0;
              
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium text-sm whitespace-nowrap border-b-2 transition-colors relative ${
                    activeTab === tab?.id
                      ? 'border-[#23395B] text-[#23395B]' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <IconComponent size={20} />
                  {tab?.label}
                  {showBadge && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>
      {/* Tab Content */}
      <div className="py-12">
        {/* Email Messages Tab */}
        {activeTab === 'email' && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <StandardCard className="">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="text-[#23395B]" size={32} />
                <h2 className="text-2xl font-bold text-gray-900">{t('community.email.title')}</h2>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-6">{t('community.email.sendTitle')}</h3>

              <div className="space-y-6">
                {/* Recipient Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('community.email.recipientLabel')} <span className="text-red-500">*</span>
                  </label>
                  <StandardInput
                    type="email"
                    placeholder={t('community.email.recipientPlaceholder')}
                    value={emailRecipient}
                    onChange={(e) => setEmailRecipient(e?.target?.value)}
                    required
                    label=""
                    error=""
                    helperText=""
                    className=""
                  />
                </div>

                {/* Template Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('community.email.templateLabel')}
                  </label>
                  <StandardSelect
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    options={emailTemplates}
                    label=""
                    error=""
                    helperText=""
                    className=""
                  />
                  <p className="mt-2 text-sm text-gray-600">
                    {t('community.email.templateHint')}
                  </p>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('community.email.subjectLabel')} <span className="text-red-500">*</span>
                  </label>
                  <StandardInput
                    type="text"
                    placeholder={t('community.email.subjectPlaceholder')}
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e?.target?.value)}
                    required
                    label=""
                    error=""
                    helperText=""
                    className=""
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('community.email.messageLabel')} <span className="text-red-500">*</span>
                  </label>
                  <StandardTextarea
                    placeholder={t('community.email.messagePlaceholder')}
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e?.target?.value)}
                    rows={8}
                    required
                    label=""
                    error=""
                    helperText=""
                    className=""
                  />
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <Lightbulb className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                  <p className="text-sm text-blue-900">
                    <strong>{t('community.email.tip')}</strong>
                  </p>
                </div>

                {/* Send Button */}
                <StandardButton
                  variant="primary"
                  size="lg"
                  onClick={handleSendEmail}
                  className="w-full"
                >
                  <Send className="mr-2" size={20} />
                  {t('community.email.sendBtn')}
                </StandardButton>

                {/* Mailto Info */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    {t('community.email.mailtoNote')}
                  </p>
                </div>
              </div>
            </StandardCard>

            {/* Sent Messages History */}
            <StandardCard className="mt-8">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="text-[#23395B]" size={28} />
                <h3 className="text-xl font-semibold text-gray-900">{t('community.email.historyTitle')}</h3>
              </div>

              {sentEmails?.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="mx-auto text-gray-300 mb-4" size={64} />
                  <p className="text-gray-500">{t('community.email.noMessages')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sentEmails?.map((email) => (
                    <div key={email?.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{email?.subject}</p>
                          <p className="text-sm text-gray-600 mt-1">{t('community.email.recipientPrefix')} {email?.recipient}</p>
                        </div>
                        <span className="text-xs text-gray-500">{email?.timestamp}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-2 py-1 bg-blue-100 text-[#23395B] text-xs rounded-full">
                          {email?.template}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </StandardCard>
          </div>
        )}

        {/* Direct Communication Tab */}
        {activeTab === 'messages' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 h-[calc(100vh-300px)]">
              {/* Conversations List */}
              <div className="md:col-span-4 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                <div className="p-4 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder={t('community.messages.searchPlaceholder')}
                      value={messageSearchTerm}
                      onChange={(e) => setMessageSearchTerm(e?.target?.value)}
                      onKeyPress={(e) => e?.key === 'Enter' && handleMessageSearch()}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#23395B] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#23395B]"></div>
                    </div>
                  ) : negotiations?.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <p className="text-gray-500">{t('community.messages.noNegotiations')}</p>
                    </div>
                  ) : (
                    negotiations?.map((negotiation) => {
                      const otherParticipant = getOtherParticipant(negotiation);
                      return (
                        <button
                          key={negotiation?.id}
                          onClick={() => setSelectedNegotiation(negotiation)}
                          className={`w-full p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left ${
                            selectedNegotiation?.id === negotiation?.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 bg-[#23395B] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                              {otherParticipant?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 truncate">
                                {otherParticipant?.fullName || t('community.messages.userFallback')}
                              </p>
                              <p className="text-sm text-gray-600 truncate">
                                {negotiation?.wish?.voucher?.title || t('community.messages.negotiationFallback')}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatDistanceToNow(new Date(negotiation.updatedAt), {
                                  addSuffix: true,
                                  locale: sr
                                })}
                              </p>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(negotiation?.status)}`}>
                              {negotiation?.status}
                            </span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Chat Area */}
              <div className="md:col-span-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                {selectedNegotiation ? (
                  <>
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#23395B] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                          {getOtherParticipant(selectedNegotiation)?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {getOtherParticipant(selectedNegotiation)?.fullName || t('community.messages.userFallback')}
                          </p>
                          <p className="text-sm text-gray-600">
                            {selectedNegotiation?.wish?.voucher?.title || t('community.messages.negotiationFallback')}
                          </p>
                          {typingUsers?.length > 0 && (
                            <p className="text-xs text-[#365A8C] italic">
                              {t('community.messages.typingIndicator', { name: typingUsers?.[0]?.userName })}
                            </p>
                          )}
                        </div>
                      </div>
                      <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                        <MoreVertical size={20} />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {messages?.map((message) => {
                        const isOwnMessage = message?.senderId === user?.id;
                        return (
                          <div
                            key={message?.id}
                            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] rounded-lg px-4 py-2 ${
                                isOwnMessage
                                  ? 'bg-[#23395B] text-white' : 'bg-gray-100 text-gray-900'
                              }`}
                            >
                              <p className="text-sm break-words whitespace-pre-wrap">
                                {message?.messageContent}
                              </p>
                              <div className={`flex items-center gap-1 mt-1 text-xs ${
                                isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                              }`}>
                                <span>
                                  {formatDistanceToNow(new Date(message.createdAt), {
                                    addSuffix: true,
                                    locale: sr
                                  })}
                                </span>
                                {isOwnMessage && (
                                  <span className="flex items-center gap-0.5">
                                    {message?.isRead ? (
                                      <>
                                        <CheckCheck size={14} />
                                        {message?.readAt && (
                                          <span className="text-xs ml-1">
                                            {formatDistanceToNow(new Date(message.readAt), {
                                              addSuffix: true,
                                              locale: sr
                                            })}
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <Check size={14} />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {typingUsers?.length > 0 && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 rounded-lg px-4 py-2">
                            <div className="flex gap-1">
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    <div className="p-4 border-t border-gray-200">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={handleInputChange}
                          onKeyPress={(e) => e?.key === 'Enter' && !e?.shiftKey && handleSendMessage()}
                          placeholder={t('community.messages.messagePlaceholder')}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#23395B] focus:border-transparent"
                        />
                        <button
                          onClick={() => handleSendMessage()}
                          disabled={sending || !newMessage?.trim()}
                          className="px-4 py-2 bg-[#23395B] text-white rounded-lg hover:bg-[#365A8C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Send size={20} />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500">
                    <p>{t('community.messages.selectNegotiation')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Negotiations Dashboard Tab */}
        {activeTab === 'negotiations' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <MessageSquare className="w-8 h-8 text-[#23395B]" />
                {t('community.negotiations.title')}
              </h2>
              <p className="mt-2 text-gray-600">
                {t('community.negotiations.subtitle')}
              </p>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-2xl font-bold text-gray-900">{analytics?.total}</div>
                <div className="text-sm text-gray-600">{t('community.negotiations.total')}</div>
              </div>
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                <div className="text-2xl font-bold text-blue-900">{analytics?.initiated}</div>
                <div className="text-sm text-blue-600">{t('community.negotiations.initiated')}</div>
              </div>
              <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
                <div className="text-2xl font-bold text-yellow-900">{analytics?.inProgress}</div>
                <div className="text-sm text-yellow-600">{t('community.negotiations.inProgress')}</div>
              </div>
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                <div className="text-2xl font-bold text-blue-900">{analytics?.agreed}</div>
                <div className="text-sm text-blue-600">{t('community.negotiations.agreed')}</div>
              </div>
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="text-2xl font-bold text-gray-900">{analytics?.completed}</div>
                <div className="text-sm text-gray-600">{t('community.negotiations.completed')}</div>
              </div>
              <div className="bg-red-50 rounded-lg border border-red-200 p-4">
                <div className="text-2xl font-bold text-red-900">{analytics?.cancelled}</div>
                <div className="text-sm text-red-600">{t('community.negotiations.cancelled')}</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder={t('community.negotiations.searchPlaceholder')}
                    value={negotiationFilters?.searchTerm}
                    onChange={(e) => setNegotiationFilters({ ...negotiationFilters, searchTerm: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#23395B] focus:border-transparent"
                  />
                </div>

                <div className="flex gap-2 flex-wrap">
                  {['all', 'initiated', 'in_progress', 'agreed']?.map((status) => (
                    <button
                      key={status}
                      onClick={() => setNegotiationFilters({ ...negotiationFilters, status })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        negotiationFilters?.status === status 
                          ? status === 'all' ? 'bg-[#23395B] text-white' 
                          : status === 'initiated' ? 'bg-blue-600 text-white'
                          : status === 'in_progress'? 'bg-yellow-600 text-white' :'bg-[#23395B] text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {status === 'all' ? t('common.all') : getStatusLabel(status)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {filteredNegotiations?.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {negotiationFilters?.searchTerm || negotiationFilters?.status !== 'all' ? t('community.negotiations.noResults') : t('community.negotiations.noNegotiations')}
                  </h3>
                  <p className="text-gray-600">
                    {negotiationFilters?.searchTerm || negotiationFilters?.status !== 'all' ? t('community.negotiations.noResultsHint') : t('community.negotiations.startOnBoard')}
                  </p>
                </div>
              ) : (
                filteredNegotiations?.map((negotiation) => (
                  <div
                    key={negotiation?.id}
                    onClick={() => handleNegotiationClick(negotiation)}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Package className="w-5 h-5 text-gray-600" />
                          <h3 className="font-semibold text-gray-900">{negotiation?.voucherTitle}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(negotiation?.status)}`}>
                            {getStatusIcon(negotiation?.status)}
                            {getStatusLabel(negotiation?.status)}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                          <User className="w-4 h-4" />
                          <span>
                            {negotiation?.isInitiator ? t('community.negotiations.withPrefix') : t('community.negotiations.fromPrefix')}
                            {negotiation?.counterpartyName}
                          </span>
                        </div>

                        {negotiation?.lastMessage && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-sm text-gray-700 line-clamp-2">
                              {negotiation?.lastMessage}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(negotiation?.updatedAt)}
                        </span>
                        {negotiation?.unreadCount > 0 && (
                          <span className="bg-blue-600 text-white text-xs font-medium px-2 py-1 rounded-full">
                            {t('community.negotiations.newCount', { count: negotiation?.unreadCount })}
                          </span>
                        )}
                        {negotiation?.currentOffer && (
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">
                              {negotiation?.currentOffer?.price} RSD
                            </div>
                            <div className="text-xs text-gray-600">{t('community.negotiations.currentOffer')}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200 flex gap-3">
                      <StandardButton className="flex-1" onClick={() => {}} variant="primary">
                        {t('community.negotiations.openConversation')}
                      </StandardButton>
                      {negotiation?.status === 'in_progress' && (
                        <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium">
                          {t('community.negotiations.quickReply')}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Notification Preferences Tab */}
        {activeTab === 'notifications' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Bell className="w-8 h-8 text-[#23395B]" />
                {t('community.notifications.title')}
              </h2>
              <p className="mt-2 text-gray-600">
                {t('community.notifications.subtitle')}
              </p>
            </div>

            {success && (
              <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg flex items-center gap-2">
                <Check className="w-5 h-5" />
                {success}
              </div>
            )}

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="flex gap-1 p-2 border-b">
                {['trading', 'wish', 'community', 'system']?.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setNotificationTab(tab)}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      notificationTab === tab
                        ? 'bg-blue-50 text-[#23395B]'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {t(`community.notifications.tabs.${tab}`)}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#23395B]"></div>
                  </div>
                ) : preferences ? (
                  <>
                    {renderCategorySection('trading', t('community.notifications.categories.trading'), tradingItems)}
                    {renderCategorySection('wish', t('community.notifications.categories.wish'), wishItems)}
                    {renderCategorySection('community', t('community.notifications.categories.community'), communityItems)}
                    {renderCategorySection('system', t('community.notifications.categories.system'), systemItems)}

                    {notificationTab === 'trading' && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('community.notifications.advanced')}</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('community.notifications.frequencyLabel')}
                            </label>
                            <select
                              value={preferences?.notificationFrequency || 'instant'}
                              onChange={(e) => handleAdvancedUpdate('notificationFrequency', e?.target?.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#23395B]"
                            >
                              <option value="instant">{t('community.notifications.frequencyInstant')}</option>
                              <option value="hourly">{t('community.notifications.frequencyHourly')}</option>
                              <option value="daily">{t('community.notifications.frequencyDaily')}</option>
                            </select>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('community.notifications.quietHoursStart')}
                              </label>
                              <input
                                type="time"
                                value={preferences?.quietHoursStart || '22:00'}
                                onChange={(e) => handleAdvancedUpdate('quietHoursStart', e?.target?.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#23395B]"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('community.notifications.quietHoursEnd')}
                              </label>
                              <input
                                type="time"
                                value={preferences?.quietHoursEnd || '08:00'}
                                onChange={(e) => handleAdvancedUpdate('quietHoursEnd', e?.target?.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#23395B]"
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                            <div>
                              <p className="font-medium text-gray-900">{t('community.notifications.urgentOnly')}</p>
                              <p className="text-sm text-gray-600">{t('community.notifications.urgentOnlyDesc')}</p>
                            </div>
                            <button
                              onClick={() => handleAdvancedUpdate('priorityUrgentOnly', !preferences?.priorityUrgentOnly)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                preferences?.priorityUrgentOnly ? 'bg-[#23395B]' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  preferences?.priorityUrgentOnly ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {t('community.notifications.loadError')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Dispute Resolution Tab */}
        {activeTab === 'disputes' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                    {t('community.disputes.title')}
                  </h2>
                  <p className="mt-2 text-gray-600">
                    {t('community.disputes.subtitle')}
                  </p>
                </div>
                <button
                  onClick={() => { setShowNewDisputeForm(true); loadUserNegotiationsForDispute(); }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm"
                >
                  <Plus size={18} />
                  {t('community.disputes.newDisputeBtn')}
                </button>
              </div>
            </div>

            {disputeSuccess && (
              <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-blue-800 font-medium">{disputeSuccess}</p>
              </div>
            )}

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 font-medium">{t('common.error')}</p>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* New Dispute Form Modal */}
            {showNewDisputeForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{t('community.disputes.form.title')}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t('community.disputes.form.subtitle')}</p>
                    </div>
                    <button onClick={() => setShowNewDisputeForm(false)} className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0">
                      <X size={24} />
                    </button>
                  </div>
                  <div className="p-6 space-y-5">
                    {/* Related Negotiation */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t('community.disputes.form.negotiationLabel')} <span className="text-gray-400 text-xs">{t('community.disputes.form.negotiationOptional')}</span>
                      </label>
                      <select
                        value={newDisputeForm?.negotiationId}
                        onChange={(e) => handleNewDisputeNegotiationChange(e?.target?.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                      >
                        <option value="">{t('community.disputes.form.negotiationPlaceholder')}</option>
                        {userNegotiations?.map(neg => (
                          <option key={neg?.id} value={neg?.id}>
                            {neg?.offerName || t('community.messages.negotiationFallback')} {t('community.disputes.form.negotiationWith', { name: neg?.counterpartyName })}
                          </option>
                        ))}
                      </select>
                    </div>

                    {!newDisputeForm?.negotiationId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {t('community.disputes.form.respondentLabel')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder={t('community.disputes.form.respondentPlaceholder')}
                          value={newDisputeForm?.respondentId}
                          onChange={(e) => setNewDisputeForm(prev => ({ ...prev, respondentId: e?.target?.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>
                    )}
                    {newDisputeForm?.negotiationId && newDisputeForm?.respondentId && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-800">
                        {t('community.disputes.form.respondentInfo', { name: userNegotiations?.find(n => n?.id === newDisputeForm?.negotiationId)?.counterpartyName || newDisputeForm?.respondentId })}
                      </div>
                    )}

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t('community.disputes.form.categoryLabel')} <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newDisputeForm?.category}
                        onChange={(e) => setNewDisputeForm(prev => ({ ...prev, category: e?.target?.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                      >
                        <option value="">{t('community.disputes.form.categoryPlaceholder')}</option>
                        <option value="payment_issue">{t('community.disputes.form.categories.payment_issue')}</option>
                        <option value="voucher_validity">{t('community.disputes.form.categories.voucher_validity')}</option>
                        <option value="miscommunication">{t('community.disputes.form.categories.miscommunication')}</option>
                        <option value="fraud_suspicion">{t('community.disputes.form.categories.fraud_suspicion')}</option>
                        <option value="delivery_failure">{t('community.disputes.form.categories.delivery_failure')}</option>
                        <option value="quality_dispute">{t('community.disputes.form.categories.quality_dispute')}</option>
                        <option value="other">{t('community.disputes.form.categories.other')}</option>
                      </select>
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('community.disputes.form.priorityLabel')}</label>
                      <select
                        value={newDisputeForm?.priority}
                        onChange={(e) => setNewDisputeForm(prev => ({ ...prev, priority: e?.target?.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                      >
                        <option value="low">{t('community.disputes.form.priorities.low')}</option>
                        <option value="medium">{t('community.disputes.form.priorities.medium')}</option>
                        <option value="high">{t('community.disputes.form.priorities.high')}</option>
                        <option value="urgent">{t('community.disputes.form.priorities.urgent')}</option>
                      </select>
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t('community.disputes.form.titleLabel')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder={t('community.disputes.form.titlePlaceholder')}
                        value={newDisputeForm?.title}
                        onChange={(e) => setNewDisputeForm(prev => ({ ...prev, title: e?.target?.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                        maxLength={200}
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t('community.disputes.form.descriptionLabel')} <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        placeholder={t('community.disputes.form.descriptionPlaceholder')}
                        value={newDisputeForm?.description}
                        onChange={(e) => setNewDisputeForm(prev => ({ ...prev, description: e?.target?.value }))}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm resize-none"
                      />
                    </div>

                    {/* Desired Resolution */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('community.disputes.form.resolutionLabel')}</label>
                      <textarea
                        placeholder={t('community.disputes.form.resolutionPlaceholder')}
                        value={newDisputeForm?.desiredResolution}
                        onChange={(e) => setNewDisputeForm(prev => ({ ...prev, desiredResolution: e?.target?.value }))}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm resize-none"
                      />
                    </div>

                    {/* Evidence Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('community.disputes.form.evidenceLabel')}</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-red-400 transition-colors">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-600 mb-2">{t('community.disputes.form.evidenceHint')}</p>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*,application/pdf,video/mp4"
                            className="hidden"
                            onChange={(e) => setNewDisputeFile(e?.target?.files?.[0] || null)}
                          />
                          <span className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg inline-block transition-colors">
                            {t('community.disputes.form.chooseFile')}
                          </span>
                        </label>
                        {newDisputeFile && (
                          <p className="mt-2 text-sm text-blue-700 font-medium">✓ {newDisputeFile?.name}</p>
                        )}
                      </div>
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => setShowNewDisputeForm(false)}
                        className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                      >
                        {t('community.disputes.form.cancelBtn')}
                      </button>
                      <button
                        onClick={handleSubmitNewDispute}
                        disabled={submittingDispute}
                        className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {submittingDispute ? (
                          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {t('community.disputes.form.submitting')}</>
                        ) : (
                          <><AlertCircle className="w-4 h-4" /> {t('community.disputes.form.submitBtn')}</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('community.disputes.yourDisputes')}</h3>
                      <p className="text-sm text-gray-600">{t('community.disputes.totalCases', { count: disputes?.length })}</p>
                    </div>
                    <div className="divide-y max-h-[600px] overflow-y-auto">
                      {disputes?.length === 0 ? (
                        <div className="p-8 text-center">
                          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-500">{t('community.disputes.noDisputes')}</p>
                          <p className="text-sm text-gray-500 mt-1">{t('community.disputes.noDisputesHint')}</p>
                        </div>
                      ) : (
                        disputes?.map((dispute) => (
                          <div
                            key={dispute?.id}
                            onClick={() => setSelectedDispute(dispute)}
                            className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                              selectedDispute?.id === dispute?.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-gray-900 text-sm line-clamp-1">{dispute?.title}</h4>
                              <span className={`text-xs font-medium px-2 py-1 rounded ${getPriorityColor(dispute?.priority)}`}>
                                {dispute?.priority}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(dispute?.disputeStatus)}`}>
                                {dispute?.disputeStatus?.replace('_', ' ')}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-2 mb-2">{dispute?.description}</p>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>{t('community.disputes.overview.filedDate')}: {formatDate(dispute?.createdAt)}</span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {dispute?.estimatedResolutionDays}d
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  {selectedDispute ? (
                    <div className="bg-white rounded-lg shadow-sm">
                      <div className="border-b">
                        <div className="flex gap-1 p-1">
                          {['overview', 'evidence', 'timeline']?.map((tab) => (
                            <button
                              key={tab}
                              onClick={() => setDisputeTab(tab)}
                              className={`flex-1 px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                                disputeTab === tab
                                  ? 'bg-blue-50 text-blue-700 border-blue-700' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                              }`}
                            >
                              {t(`community.disputes.tabs.${tab}`)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="p-6">
                        {disputeTab === 'overview' && (
                          <div className="space-y-6">
                            <div>
                              <div className="flex items-start justify-between mb-4">
                                <div>
                                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedDispute?.title}</h3>
                                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                                    <User size={14} />
                                    <span>{selectedDispute?.filedByProfile?.full_name || 'N/A'}</span>
                                    <span>•</span>
                                    <span>{selectedDispute?.createdAt ? formatDistanceToNow(new Date(selectedDispute?.createdAt), { addSuffix: true, locale: sr }) : ''}</span>
                                  </div>
                                  <p className="text-gray-700 whitespace-pre-wrap">{selectedDispute?.description}</p>
                                </div>
                                {selectedDispute?.disputeStatus !== 'escalated' && selectedDispute?.disputeStatus !== 'resolved' && (
                                  <button
                                    onClick={() => handleEscalate(selectedDispute?.id)}
                                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                  >
                                    <ArrowUpCircle className="w-4 h-4" />
                                    {t('community.disputes.overview.escalateBtn')}
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('community.disputes.overview.caseInfo')}</h4>
                                <div className="space-y-3">
                                  <div>
                                    <p className="text-xs text-gray-500">{t('community.disputes.overview.category')}</p>
                                    <p className="text-sm font-medium text-gray-900">{selectedDispute?.disputeCategory?.replace('_', ' ')}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">{t('community.disputes.overview.filedDate')}</p>
                                    <p className="text-sm font-medium text-gray-900">{formatDate(selectedDispute?.createdAt)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">{t('community.disputes.overview.estimatedResolution')}</p>
                                    <p className="text-sm font-medium text-gray-900">{selectedDispute?.estimatedResolutionDays} {t('community.disputes.overview.days')}</p>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('community.disputes.overview.parties')}</h4>
                                <div className="space-y-3">
                                  <div>
                                    <p className="text-xs text-gray-500">{t('community.disputes.overview.filedBy')}</p>
                                    <p className="text-sm font-medium text-gray-900">{selectedDispute?.filedByProfile?.full_name || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">{t('community.disputes.overview.respondent')}</p>
                                    <p className="text-sm font-medium text-gray-900">{selectedDispute?.respondentProfile?.full_name || 'N/A'}</p>
                                  </div>
                                  {selectedDispute?.mediatorProfile && (
                                    <div>
                                      <p className="text-xs text-gray-500">{t('community.disputes.overview.mediator')}</p>
                                      <p className="text-sm font-medium text-gray-900">{selectedDispute?.mediatorProfile?.full_name}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('community.disputes.overview.description')}</h4>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedDispute?.description}</p>
                            </div>

                            {selectedDispute?.desiredResolution && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('community.disputes.overview.desiredResolution')}</h4>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedDispute?.desiredResolution}</p>
                              </div>
                            )}

                            {selectedDispute?.resolutionDetails && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4" />
                                  {t('community.disputes.overview.resolution')}
                                </h4>
                                <p className="text-sm text-blue-800 whitespace-pre-wrap">{selectedDispute?.resolutionDetails}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {disputeTab === 'evidence' && (
                          <div className="space-y-6">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                              <h4 className="text-sm font-medium text-gray-900 mb-2">{t('community.disputes.evidence.uploadTitle')}</h4>
                              <p className="text-xs text-gray-600 mb-4">
                                {t('community.disputes.evidence.uploadHint')}
                              </p>
                              <label className="inline-block">
                                <input
                                  type="file"
                                  onChange={handleEvidenceUpload}
                                  accept="image/*,application/pdf,video/mp4"
                                  disabled={uploadingEvidence}
                                  className="hidden"
                                />
                                <span className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 cursor-pointer inline-block">
                                  {uploadingEvidence ? t('community.disputes.evidence.uploading') : t('community.disputes.evidence.chooseFile')}
                                </span>
                              </label>
                            </div>

                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-4">{t('community.disputes.evidence.submittedTitle', { count: evidence?.length })}</h4>
                              {evidence?.length === 0 ? (
                                <div className="text-center py-8">
                                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                  <p className="text-gray-600">{t('community.disputes.evidence.noEvidence')}</p>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {evidence?.map((item) => (
                                    <div key={item?.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                                      <div className="flex items-start gap-4">
                                        {item?.signedUrl && item?.mimeType?.startsWith('image/') && (
                                          <img
                                            src={item?.signedUrl}
                                            alt={item?.fileName}
                                            className="w-24 h-24 object-cover rounded"
                                          />
                                        )}
                                        <div className="flex-1">
                                          <div className="flex items-center justify-between mb-2">
                                            <p className="font-medium text-gray-900">{item?.fileName}</p>
                                            <span className={`text-xs px-2 py-1 rounded ${getPriorityColor(item?.priority)}`}>
                                              {item?.priority}
                                            </span>
                                          </div>
                                          {item?.description && (
                                            <p className="text-sm text-gray-700 mb-2">{item?.description}</p>
                                          )}
                                          <div className="flex items-center gap-4 text-xs text-gray-500">
                                            <span className="flex items-center gap-1">
                                              <User size={13} />
                                              {item?.uploader?.full_name || 'N/A'}
                                            </span>
                                            <span className="flex items-center gap-1">
                                              <MessageSquare size={13} />
                                              {item?.createdAt ? formatDistanceToNow(new Date(item?.createdAt), { addSuffix: true, locale: sr }) : ''}
                                            </span>
                                            <span>{(item?.fileSize / 1024)?.toFixed(1)} KB</span>
                                          </div>
                                          {item?.signedUrl && (
                                            <a
                                              href={item?.signedUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-block mt-2 text-sm text-blue-600 hover:text-blue-700"
                                            >
                                              {t('community.disputes.evidence.viewFile')}
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {disputeTab === 'timeline' && (
                          <div className="space-y-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-4">{t('community.disputes.timeline.title')}</h4>
                            {timeline?.length === 0 ? (
                              <div className="text-center py-8">
                                <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                <p className="text-gray-600">{t('community.disputes.timeline.noEvents')}</p>
                              </div>
                            ) : (
                              <div className="relative">
                                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                                <div className="space-y-6">
                                  {timeline?.map((event) => (
                                    <div key={event?.id} className="relative flex gap-4">
                                      <div className="absolute left-4 -translate-x-1/2 w-3 h-3 rounded-full bg-blue-600 border-2 border-white"></div>
                                      <div className="ml-10 flex-1">
                                        <div className="bg-gray-50 rounded-lg p-4">
                                          <div className="flex items-start justify-between mb-2">
                                            <p className="text-sm font-medium text-gray-900">{event?.eventDescription}</p>
                                            {event?.newStatus && (
                                              <span className={`text-xs px-2 py-1 rounded ${getStatusColor(event?.newStatus)}`}>
                                                {event?.newStatus?.replace('_', ' ')}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-3 text-xs text-gray-500">
                                            {event?.actor && (
                                              <span className="flex items-center gap-1">
                                                <Users className="w-3 h-3" />
                                                {event?.actor?.full_name}
                                              </span>
                                            )}
                                            <span>{event?.createdAt ? formatDistanceToNow(new Date(event?.createdAt), { addSuffix: true, locale: sr }) : ''}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                      <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">{t('community.disputes.selectDispute')}</h4>
                      <p className="text-gray-600">{t('community.disputes.selectDisputeHint')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Discussions Tab */}
        {activeTab === 'discussions' && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900">{t('community.discussions.title')}</h2>
              {user && !selectedTopic && (
                <StandardButton
                  onClick={() => setShowNewTopicForm(true)}
                  variant="primary"
                  className="flex items-center gap-2"
                >
                  <Plus size={18} />
                  {t('community.discussions.newTopicBtn')}
                </StandardButton>
              )}
              {selectedTopic && (
                <button
                  onClick={handleBackToTopics}
                  className="flex items-center gap-2 text-[#23395B] hover:text-[#365A8C] font-medium transition-colors"
                >
                  {t('community.discussions.backToDiscussions')}
                </button>
              )}
            </div>

            {discussionSuccess && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                {discussionSuccess}
              </div>
            )}
            {topicsError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {topicsError}
              </div>
            )}

            {/* New Topic Modal */}
            {showNewTopicForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
                  <div className="flex items-center justify-between p-6 border-b">
                    <h3 className="text-xl font-bold text-gray-900">{t('community.discussions.form.title')}</h3>
                    <button onClick={() => { setShowNewTopicForm(false); setNewTopicTitle(''); setNewTopicBody(''); }} className="text-gray-400 hover:text-gray-600">
                      <X size={24} />
                    </button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.discussions.form.titleLabel')}</label>
                      <StandardInput
                        value={newTopicTitle}
                        onChange={(e) => setNewTopicTitle(e?.target?.value)}
                        placeholder={t('community.discussions.form.titlePlaceholder')}
                        label=""
                        error=""
                        helperText=""
                        className=""
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.discussions.form.bodyLabel')}</label>
                      <textarea
                        value={newTopicBody}
                        onChange={(e) => setNewTopicBody(e?.target?.value)}
                        placeholder={t('community.discussions.form.bodyPlaceholder')}
                        rows={5}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#23395B] resize-none"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 p-6 border-t">
                    <StandardButton
                      onClick={handleCreateTopic}
                      disabled={submittingTopic || !newTopicTitle?.trim() || !newTopicBody?.trim()}
                      className="flex-1"
                      variant="primary"
                    >
                      {submittingTopic ? t('community.discussions.form.submitting') : t('community.discussions.form.submitBtn')}
                    </StandardButton>
                    <button
                      onClick={() => { setShowNewTopicForm(false); setNewTopicTitle(''); setNewTopicBody(''); }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      {t('community.discussions.form.cancelBtn')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Topic Detail View */}
            {selectedTopic && (
              <div className="space-y-6">
                {topicDetailLoading ? (
                  <div className="text-center py-12 text-gray-500">{t('community.discussions.loadingTopic')}</div>
                ) : topicDetail ? (
                  <>
                    <StandardCard className="">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">{topicDetail?.title}</h3>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                            <User size={14} />
                            <span>{topicDetail?.authorName}</span>
                            <span>•</span>
                            <span>{topicDetail?.createdAt ? formatDistanceToNow(new Date(topicDetail?.createdAt), { addSuffix: true, locale: sr }) : ''}</span>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">{topicDetail?.body}</p>
                        </div>
                        {(user?.id === topicDetail?.userId || user?.user_metadata?.role === 'admin') && (
                          <button
                            onClick={(e) => { e?.stopPropagation(); handleDeleteTopic(topicDetail?.id); }}
                            className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                    </StandardCard>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {t('community.discussions.comments', { count: topicDetail?.comments?.length || 0 })}
                      </h4>
                      {topicDetail?.comments?.length === 0 && (
                        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                          {t('community.discussions.noComments')}
                        </div>
                      )}
                      {topicDetail?.comments?.map((comment) => (
                        <StandardCard key={comment?.id} className="">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                                <User size={14} />
                                <span className="font-medium text-gray-700">{comment?.authorName}</span>
                                <span>•</span>
                                <span>{comment?.createdAt ? formatDistanceToNow(new Date(comment?.createdAt), { addSuffix: true, locale: sr }) : ''}</span>
                              </div>
                              <p className="text-gray-700 whitespace-pre-wrap">{comment?.body}</p>
                            </div>
                            {(user?.id === comment?.userId || user?.user_metadata?.role === 'admin') && (
                              <button
                                onClick={() => handleDeleteComment(comment?.id)}
                                className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                        </StandardCard>
                      ))}
                    </div>

                    {user ? (
                      <StandardCard className="">
                        <h4 className="text-base font-semibold text-gray-900 mb-3">{t('community.discussions.addComment')}</h4>
                        <textarea
                          value={newCommentBody}
                          onChange={(e) => setNewCommentBody(e?.target?.value)}
                          placeholder={t('community.discussions.commentPlaceholder')}
                          rows={3}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#23395B] resize-none mb-3"
                        />
                        <StandardButton
                          onClick={handleAddComment}
                          disabled={submittingComment || !newCommentBody?.trim()}
                          variant="primary"
                          className=""
                        >
                          {submittingComment ? t('community.discussions.submittingComment') : t('community.discussions.submitComment')}
                        </StandardButton>
                      </StandardCard>
                    ) : (
                      <div className="text-center py-6 bg-gray-50 rounded-lg text-gray-600">
                        {t('community.discussions.loginToComment')}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}

            {/* Topics List */}
            {!selectedTopic && (
              <div className="space-y-4">
                {topicsLoading && (
                  <div className="text-center py-12 text-gray-500">{t('community.discussions.loadingTopics')}</div>
                )}
                {!topicsLoading && topics?.length === 0 && (
                  <div className="text-center py-16">
                    <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('community.discussions.noTopics')}</h3>
                    <p className="text-gray-500 mb-6">
                      {user ? t('community.discussions.noTopicsLoggedIn') : t('community.discussions.noTopicsGuest')}
                    </p>
                    {user && (
                      <StandardButton onClick={() => setShowNewTopicForm(true)} className="flex items-center gap-2 mx-auto" variant="primary">
                        <Plus size={18} />
                        {t('community.discussions.startFirstTopic')}
                      </StandardButton>
                    )}
                  </div>
                )}
                {!topicsLoading && topics?.map((topic) => (
                  <StandardCard key={topic?.id} hover className="cursor-pointer" onClick={() => handleOpenTopic(topic)}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-[#23395B] transition-colors">
                          {topic?.title}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-3">{topic?.body}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <User size={13} />
                            {topic?.authorName}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageSquare size={13} />
                            {t('community.discussions.commentCount', { count: topic?.commentCount })}
                          </span>
                          <span>{topic?.createdAt ? formatDistanceToNow(new Date(topic?.createdAt), { addSuffix: true, locale: sr }) : ''}</span>
                        </div>
                      </div>
                      {(user?.id === topic?.userId || user?.user_metadata?.role === 'admin') && (
                        <button
                          onClick={(e) => { e?.stopPropagation(); handleDeleteTopic(topic?.id); }}
                          className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                        >
                          <X size={18} />
                        </button>
                      )}
                    </div>
                  </StandardCard>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Best Members Tab */}
        {activeTab === 'members' && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <StandardCard className="">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('community.members.title')}</h3>
              <p className="text-sm text-gray-500 mb-6">{t('community.members.subtitle')}</p>

              {bestMembersLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#23395B]"></div>
                  <span className="ml-3 text-gray-500">{t('community.members.loading')}</span>
                </div>
              )}

              {bestMembersError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {bestMembersError}
                </div>
              )}

              {!bestMembersLoading && !bestMembersError && bestMembers?.length === 0 && (
                <div className="text-center py-12">
                  <Users className="mx-auto text-gray-300 mb-3" size={48} />
                  <p className="text-gray-500">{t('community.members.noMembers')}</p>
                </div>
              )}

              {!bestMembersLoading && bestMembers?.length > 0 && (
                <div className="space-y-3">
                  {bestMembers?.map((member, index) => {
                    const medalColors = ['bg-yellow-400', 'bg-gray-300', 'bg-amber-600'];
                    const medalColor = index < 3 ? medalColors?.[index] : 'bg-[#23395B]';
                    const initials = member?.fullName
                      ?.split(' ')
                      ?.map(w => w?.[0])
                      ?.slice(0, 2)
                      ?.join('')
                      ?.toUpperCase() || '?';

                    return (
                      <div
                        key={member?.id}
                        className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                          index === 0
                            ? 'bg-yellow-50 border-yellow-200'
                            : index === 1
                            ? 'bg-gray-50 border-gray-200'
                            : index === 2
                            ? 'bg-amber-50 border-amber-200' :'bg-white border-gray-100 hover:bg-gray-50'
                        }`}
                      >
                        {/* Rank */}
                        <div className="w-8 text-center flex-shrink-0">
                          {index < 3 ? (
                            <span className="text-xl">{['\uD83E\uDD47', '\uD83E\uDD48', '\uD83E\uDD49']?.[index]}</span>
                          ) : (
                            <span className="text-xs font-bold text-gray-400">#{index + 1}</span>
                          )}
                        </div>
                        {/* Avatar */}
                        <div className={`w-12 h-12 ${medalColor} rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0`}>
                          {initials}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900 truncate">{member?.fullName}</p>
                            {member?.valuebookId && member?.valuebookId !== '\u2014' && (
                              <span className="text-xs bg-blue-100 text-[#23395B] px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                                ID: {member?.valuebookId}
                              </span>
                            )}
                          </div>
                          {member?.otherInfo && (
                            <p className="text-xs text-gray-500 truncate mt-0.5">{member?.otherInfo}</p>
                          )}
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <span className="text-xs text-gray-500">
                              💼 {t('community.members.completedDeals', { count: member?.completedDeals })}
                            </span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-500">
                              ⭐ {member?.ratingCount === 1 ? t('community.members.ratings', { count: member?.ratingCount }) : t('community.members.ratingsPlural', { count: member?.ratingCount })}
                            </span>
                          </div>
                        </div>
                        {/* Rating */}
                        <div className="flex-shrink-0 text-right">
                          {member?.avgRating !== null ? (
                            <>
                              <div className="flex items-center gap-1 justify-end">
                                {[1, 2, 3, 4, 5]?.map(star => (
                                  <span
                                    key={star}
                                    className={`text-base ${
                                      star <= Math.round(member?.avgRating)
                                        ? 'fill-current' :'text-gray-200'
                                    }`}
                                    style={star <= Math.round(member?.avgRating) ? {color:'#CFB53B'} : {}}
                                  >
                                    ★
                                  </span>
                                ))}
                              </div>
                              <p className="text-sm font-bold text-gray-900 mt-0.5">{member?.avgRating?.toFixed(1)}</p>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">{t('community.members.noRating')}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </StandardCard>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp size={22} className="text-[#23395B]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{t('community.stats.title')}</h2>
                <p className="text-sm text-gray-500">{t('community.stats.subtitle')}</p>
              </div>
            </div>

            {/* Period filter buttons */}
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { key: '7d', label: t('community.stats.periods.7d') },
                { key: '30d', label: t('community.stats.periods.30d') },
                { key: '1y', label: t('community.stats.periods.1y') },
                { key: 'all', label: t('community.stats.periods.all') },
              ]?.map((p) => (
                <button
                  key={p?.key}
                  onClick={() => handleStatsPeriodChange(p?.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                    statsPeriod === p?.key
                      ? 'bg-[#23395B] text-white border-[#23395B]' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {p?.label}
                </button>
              ))}
            </div>

            {/* Loading */}
            {statsLoading && (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-2 border-[#23395B] border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-gray-500">{t('community.stats.loading')}</p>
                </div>
              </div>
            )}

            {/* Error */}
            {statsError && !statsLoading && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm mb-4">
                <AlertCircle size={16} />
                {statsError}
              </div>
            )}

            {/* Stats content */}
            {!statsLoading && !statsError && statsData && (
              <div className="space-y-6">
                {/* Summary cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4">
                  <StandardCard className="">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Package size={20} className="text-[#23395B]" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 leading-tight">{t('community.stats.activeOffers')}</p>
                        <p className="text-2xl font-bold text-gray-900">{statsData?.activeOffers}</p>
                      </div>
                    </div>
                  </StandardCard>

                  <StandardCard className="">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CheckCircle size={20} className="text-[#23395B]" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 leading-tight">{t('community.stats.completedDeals')}</p>
                        <p className="text-2xl font-bold text-gray-900">{statsData?.completedDeals}</p>
                      </div>
                    </div>
                  </StandardCard>

                  <StandardCard className="">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Archive size={20} className="text-gray-500" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 leading-tight">{t('community.stats.archivedOffers')}</p>
                        <p className="text-2xl font-bold text-gray-900">{statsData?.archivedOffers}</p>
                      </div>
                    </div>
                  </StandardCard>

                  <StandardCard className="">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Users size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 leading-tight">{t('community.stats.activeUsers')}</p>
                        <p className="text-2xl font-bold text-gray-900">{statsData?.activeUsers}</p>
                      </div>
                    </div>
                  </StandardCard>

                  <StandardCard className="">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <AlertCircle size={20} className="text-orange-500" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 leading-tight">{t('community.stats.disputesOpened')}</p>
                        <p className="text-2xl font-bold text-gray-900">{statsData?.disputesOpened}</p>
                      </div>
                    </div>
                  </StandardCard>

                  <StandardCard className="">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Shield size={20} className="text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 leading-tight">{t('community.stats.disputesResolved')}</p>
                        <p className="text-2xl font-bold text-gray-900">{statsData?.disputesResolved}</p>
                      </div>
                    </div>
                  </StandardCard>
                </div>

                {/* Category breakdown + Top vouchers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Top categories */}
                  <StandardCard className="">
                    <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp size={16} className="text-[#23395B]" />
                      {t('community.stats.topCategories')}
                    </h3>
                    {statsData?.topCategories?.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">{t('community.stats.noData')}</p>
                    ) : (
                      <div className="space-y-3">
                        {statsData?.topCategories?.map((cat, idx) => {
                          const maxCount = statsData?.topCategories?.[0]?.count || 1;
                          const pct = Math.round((cat?.count / maxCount) * 100);
                          return (
                            <div key={idx}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-gray-700 truncate max-w-[70%]">{cat?.name}</span>
                                <span className="text-sm font-semibold text-gray-900">{cat?.count}</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2">
                                <div
                                  className="bg-[#23395B] h-2 rounded-full transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </StandardCard>

                  {/* Top vouchers */}
                  <StandardCard className="">
                    <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <ArrowUpCircle size={16} className="text-[#23395B]" />
                      {t('community.stats.topVouchers')}
                    </h3>
                    {statsData?.topVouchers?.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">{t('community.stats.noData')}</p>
                    ) : (
                      <div className="space-y-3">
                        {statsData?.topVouchers?.map((v, idx) => {
                          const maxCount = statsData?.topVouchers?.[0]?.count || 1;
                          const pct = Math.round((v?.count / maxCount) * 100);
                          return (
                            <div key={idx}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-gray-700 truncate max-w-[70%]">{v?.name}</span>
                                <span className="text-sm font-semibold text-gray-900">{v?.count}</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2">
                                <div
                                  className="bg-blue-400 h-2 rounded-full transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </StandardCard>
                </div>
              </div>
            )}

            {/* Empty state when no data yet */}
            {!statsLoading && !statsError && !statsData && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <TrendingUp size={52} className="text-gray-300 mb-4" />
                <p className="text-gray-500">{t('community.stats.clickPeriod')}</p>
              </div>
            )}
          </div>
        )}

        {/* Tips Tab */}
        {activeTab === 'tips' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('community.tips.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tips?.map((tip, index) => {
                const IconComponent = tip?.icon;
                return (
                  <StandardCard key={index} className="">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-[#23395B]" size={24} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">{tip?.title}</h3>
                        <p className="text-sm text-gray-600">{tip?.description}</p>
                      </div>
                    </div>
                  </StandardCard>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {/* Search Results Modal */}
      {showSearchResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('community.messages.searchPlaceholder')}: {searchResults?.length}
              </h3>
              <button
                onClick={closeSearchResults}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {searchResults?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {t('community.messages.searchPlaceholder')}: "{messageSearchTerm}"
                </p>
              ) : (
                searchResults?.map((result) => (
                  <button
                    key={result?.id}
                    onClick={() => selectSearchResult(result)}
                    className="w-full text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#23395B] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        {result?.sender?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold text-gray-900">
                            {result?.sender?.fullName || t('community.messages.userFallback')}
                          </p>
                          <span className="text-xs text-gray-400">
                            {formatDistanceToNow(new Date(result.createdAt), {
                              addSuffix: true,
                              locale: sr
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          {result?.negotiation?.voucherTitle}
                        </p>
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {result?.messageContent}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">{t('community.footer')}</p>
        </div>
      </footer>
    </div>
  );
};

export default Community;